import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../supabase/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { 
  FiSun, FiMoon, FiMonitor, FiUser, FiCpu, 
  FiShield, FiBell, FiLock, FiKey, FiSave, 
  FiServer, FiLogOut, FiZap, FiAward, FiCheck,
  FiTarget, FiZapOff, FiTrendingUp, FiCrosshair,
  FiActivity, FiCommand, FiGlobe, FiBriefcase
} = FiIcons

// --- SUB-COMPONENT: PROFILE CARD ---
const ProfileCard = ({ user, profile, onSignOut }) => {
  const AvatarIcon = FiIcons[profile?.avatar_url] || FiUser;
  
  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative group"
    >
      <div className="absolute -inset-1 bg-gradient-to-r from-lux-primary to-lux-accent rounded-[2rem] blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
      <div className="relative bg-white dark:bg-lux-dark-card rounded-[2rem] border border-lux-light-border dark:border-lux-dark-border overflow-hidden shadow-2xl">
        <div className="h-32 bg-gradient-to-br from-slate-900 via-lux-dark-bg to-lux-primary/20 relative overflow-hidden">
          <div className="absolute top-4 right-6 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/20">
            <span className="text-[10px] font-black text-white uppercase tracking-widest">Premium Member</span>
          </div>
        </div>
        <div className="px-8 pb-8">
          <div className="relative -mt-12 mb-6">
            <div className="h-24 w-24 rounded-3xl bg-gradient-to-br from-lux-primary to-lux-accent p-1 shadow-2xl">
              <div className="h-full w-full rounded-[1.4rem] bg-lux-dark-bg flex items-center justify-center text-4xl text-white border border-white/10">
                <SafeIcon icon={AvatarIcon} className="h-10 w-10" />
              </div>
            </div>
          </div>
          <div className="space-y-1 mb-8">
            <h3 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">
              {profile?.display_name || 'Professional Trader'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium truncate">{user?.email}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-lux-light-border dark:border-lux-dark-border">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Rank</p>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAward} className="h-4 w-4 text-lux-primary" />
                <span className="text-xs font-bold dark:text-white capitalize">{profile?.trading_experience || 'Pro'}</span>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-lux-light-border dark:border-lux-dark-border">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Status</p>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-bold dark:text-white">Active</span>
              </div>
            </div>
          </div>
          <button 
            onClick={onSignOut} 
            className="w-full flex items-center justify-center space-x-2 py-4 rounded-2xl bg-[#F08080] hover:bg-[#f29191] text-white font-black transition-all shadow-lg shadow-[#F08080]/20 hover:shadow-[#F08080]/40 hover:-translate-y-0.5 active:scale-95"
          >
            <SafeIcon icon={FiLogOut} className="h-4 w-4" />
            <span>Sign Out Session</span>
          </button>
        </div>
      </div>
    </motion.div>
  )
}

// --- MAIN COMPONENT ---
const Settings = () => {
  const { theme, setTheme } = useTheme()
  const { user, profile, signOut } = useAuth()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const [aiSettings, setAiSettings] = useState({
    provider: 'quest',
    apiKey: '',
    model: ''
  })

  const [avatar, setAvatar] = useState('FiUser')

  useEffect(() => {
    if (profile) {
      setAiSettings({
        provider: profile.ai_provider || 'quest',
        apiKey: profile.ai_api_key || '',
        model: profile.ai_model || ''
      })
      setAvatar(profile.avatar_url || 'FiUser')
    }
  }, [profile])

  const avatarOptions = [
    { id: 'FiUser', icon: FiUser },
    { id: 'FiZap', icon: FiZap },
    { id: 'FiTarget', icon: FiTarget },
    { id: 'FiTrendingUp', icon: FiTrendingUp },
    { id: 'FiCrosshair', icon: FiCrosshair },
    { id: 'FiActivity', icon: FiActivity },
    { id: 'FiCommand', icon: FiCommand },
    { id: 'FiGlobe', icon: FiGlobe },
    { id: 'FiBriefcase', icon: FiBriefcase }
  ]

  const handleSaveSettings = async () => {
    setLoading(true)
    setMessage({ type: '', text: '' })
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ai_provider: aiSettings.provider,
          ai_api_key: aiSettings.apiKey,
          ai_model: aiSettings.model,
          avatar_url: avatar
        })
        .eq('user_id', user.id)

      if (error) throw error
      setMessage({ type: 'success', text: 'Settings Updated' })
    } catch (err) {
      setMessage({ type: 'error', text: 'Error Saving' })
    } finally {
      setLoading(false)
      setTimeout(() => setMessage({ type: '', text: '' }), 3000)
    }
  }

  const providers = [
    { id: 'quest', label: 'Quest' },
    { id: 'openai', label: 'OpenAI' },
    { id: 'openrouter', label: 'OpenRouter' },
    { id: 'anthropic', label: 'Anthropic' },
    { id: 'google', label: 'Google' }
  ]

  const themeOptions = [
    { id: 'light', label: 'Clarity', icon: FiSun, desc: 'High-contrast professional focus.' },
    { id: 'dark', label: 'Obsidian', icon: FiMoon, desc: 'Deep-space aesthetics for long sessions.' },
    { id: 'system', label: 'Adaptive', icon: FiMonitor, desc: 'Syncs with your device environment.' }
  ]

  return (
    <div className="max-w-[1400px] mx-auto pb-24 px-4 sm:px-6">
      <header className="mb-12">
        <div className="flex items-center space-x-4 mb-4">
          <span className="h-px w-12 bg-lux-primary"></span>
          <span className="text-[10px] font-black text-lux-primary uppercase tracking-[0.4em]">System Control</span>
        </div>
        <h1 className="text-5xl font-black tracking-tighter text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-300 dark:to-gray-500">
          Preferences
        </h1>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-12">
        <div className="xl:col-span-8 space-y-12">
          
          {/* PROFILE AVATAR SECTION */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-lux-dark-card rounded-[2.5rem] border border-lux-light-border dark:border-lux-dark-border p-8 sm:p-10 shadow-xl"
          >
            <div className="flex items-center space-x-4 mb-10">
              <div className="p-2.5 bg-lux-primary/10 rounded-xl">
                <SafeIcon icon={FiUser} className="h-6 w-6 text-lux-primary" />
              </div>
              <div className="space-y-1">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Identity Icon</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Select your trading persona icon</p>
              </div>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-4">
              {avatarOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setAvatar(opt.id)}
                  className={`flex items-center justify-center p-4 rounded-2xl border-2 transition-all duration-300 ${
                    avatar === opt.id 
                      ? 'border-lux-primary bg-lux-primary/5 shadow-inner' 
                      : 'border-lux-light-border dark:border-lux-dark-border hover:border-gray-300 dark:hover:border-slate-600'
                  }`}
                >
                  <SafeIcon icon={opt.icon} className={`h-6 w-6 ${avatar === opt.id ? 'text-lux-primary' : 'text-gray-400'}`} />
                </button>
              ))}
            </div>
          </motion.section>

          {/* AI CONFIGURATION SECTION */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-lux-dark-card rounded-[2.5rem] border border-lux-light-border dark:border-lux-dark-border p-8 sm:p-10 shadow-xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none">
              <SafeIcon icon={FiCpu} className="h-32 w-32 dark:text-white" />
            </div>

            <header className="flex items-center justify-between mb-10">
              <div className="space-y-1">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-purple-500/10 rounded-xl">
                    <SafeIcon icon={FiCpu} className="h-6 w-6 text-purple-500" />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">AI Engine (BYOK)</h2>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Connect your favorite AI providers via API</p>
              </div>
            </header>

            <div className="space-y-8">
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Intelligence Provider</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {providers.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setAiSettings({...aiSettings, provider: p.id})}
                      className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all duration-300 ${
                        aiSettings.provider === p.id 
                          ? 'border-lux-primary bg-lux-primary/5 shadow-inner' 
                          : 'border-lux-light-border dark:border-lux-dark-border hover:border-gray-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <SafeIcon icon={FiServer} className={`h-5 w-5 mb-2 ${aiSettings.provider === p.id ? 'text-lux-primary' : 'text-gray-400'}`} />
                      <span className={`text-[10px] font-black uppercase tracking-tighter ${aiSettings.provider === p.id ? 'text-lux-primary' : 'text-gray-500'}`}>
                        {p.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Preferred Model</label>
                  <input 
                    type="text" 
                    value={aiSettings.model}
                    onChange={(e) => setAiSettings({...aiSettings, model: e.target.value})}
                    placeholder="e.g. gpt-4o"
                    className="lux-input !bg-gray-50/50 dark:!bg-white dark:!text-black border-2 border-gray-100 dark:border-white/20 transition-all"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">API Key</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                      <SafeIcon icon={FiKey} className="h-4 w-4 text-gray-400 group-focus-within:text-lux-primary transition-colors" />
                    </div>
                    <input 
                      type="password" 
                      value={aiSettings.apiKey}
                      onChange={(e) => setAiSettings({...aiSettings, apiKey: e.target.value})}
                      className="lux-input pl-14 !bg-gray-50/50 dark:!bg-white dark:!text-black border-2 border-gray-100 dark:border-white/20 transition-all"
                      placeholder="sk-••••••••••••"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* APPEARANCE SECTION */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-lux-dark-card rounded-[2.5rem] border border-lux-light-border dark:border-lux-dark-border p-8 sm:p-10 shadow-xl"
          >
            <div className="flex items-center space-x-4 mb-10">
              <div className="p-2.5 bg-lux-primary/10 rounded-xl">
                <SafeIcon icon={FiMonitor} className="h-6 w-6 text-lux-primary" />
              </div>
              <div className="space-y-1">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Visual Interface</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Customize your trading environment</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {themeOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setTheme(opt.id)}
                  className={`group relative flex flex-col p-6 rounded-[2rem] border-2 transition-all duration-500 ${
                    theme === opt.id 
                      ? 'border-lux-primary bg-lux-primary/[0.03] shadow-lg shadow-lux-primary/5' 
                      : 'border-lux-light-border dark:border-lux-dark-border hover:border-gray-300 dark:hover:border-slate-600 bg-gray-50/50 dark:bg-slate-900/30'
                  }`}
                >
                  <div className={`p-4 rounded-2xl mb-6 self-start transition-all duration-500 ${
                    theme === opt.id ? 'bg-lux-primary text-white shadow-xl rotate-3' : 'bg-white dark:bg-slate-800 text-gray-500'
                  }`}>
                    <SafeIcon icon={opt.icon} className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-black text-gray-900 dark:text-white tracking-tight">{opt.label}</h3>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 font-medium leading-relaxed">{opt.desc}</p>
                  </div>
                  {theme === opt.id && (
                    <div className="absolute top-4 right-4 bg-lux-primary text-white p-1 rounded-full shadow-lg">
                      <SafeIcon icon={FiCheck} className="h-3 w-3" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </motion.section>

          {/* GLOBAL SAVE BUTTON */}
          <div className="flex items-center justify-between p-6 bg-white dark:bg-lux-dark-card border border-lux-light-border dark:border-lux-dark-border rounded-[2.5rem] shadow-xl">
             <div className="hidden sm:flex items-center space-x-3 text-gray-500">
               <SafeIcon icon={FiShield} className="h-4 w-4" />
               <span className="text-xs font-medium">Changes are synced to your secure cloud profile</span>
             </div>
             <button 
                onClick={handleSaveSettings}
                disabled={loading}
                className="w-full sm:w-auto min-w-[200px] flex items-center justify-center space-x-3 py-4 px-8 rounded-2xl text-lg font-black bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50"
              >
                <SafeIcon icon={loading ? FiCpu : FiSave} className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                <span className="tracking-tight">{loading ? 'Saving...' : 'Save All Preferences'}</span>
              </button>
          </div>
        </div>

        <div className="xl:col-span-4">
          <div className="sticky top-32 space-y-8">
            <ProfileCard user={user} profile={profile} onSignOut={signOut} />
            
            <AnimatePresence>
              {message.text && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`p-4 rounded-2xl border flex items-center space-x-3 ${
                    message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-500' : 'bg-red-500/10 border-red-500/20 text-red-500'
                  }`}
                >
                  <SafeIcon icon={message.type === 'success' ? FiCheck : FiZapOff} className="h-5 w-5" />
                  <span className="text-sm font-bold">{message.text}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-lux-primary/5 rounded-3xl p-8 border border-lux-primary/10">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiShield} className="h-5 w-5 text-lux-primary" />
                <h4 className="font-bold text-gray-900 dark:text-white">Security Protocol</h4>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                Your profile data and AI configurations are encrypted using AES-256. Identity icons help personalize your dashboard without using PII like photos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings